import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const REFRESH_MS = 12000;

function sinceText(iso) {
  if (!iso) return "";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  return new Date(iso).toLocaleDateString();
}

function hoursLeft(lastInAt) {
  if (!lastInAt) return 0;
  const left = 24 - (Date.now() - new Date(lastInAt).getTime()) / 3600000;
  return Math.max(0, Math.round(left));
}

export function WhatsAppInbox({ metaStatus }) {
  const [threads, setThreads] = useState([]);
  const [openPhone, setOpenPhone] = useState("");
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const connected = !!metaStatus?.configured;

  const loadThreads = useCallback(() => {
    api.get("/admin/whatsapp/inbox").then(({ data }) => setThreads(data)).catch(() => {});
  }, []);

  const loadThread = useCallback((phone) => {
    api.get(`/admin/whatsapp/inbox/${phone}`)
      .then(({ data }) => setThread(data))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  useEffect(() => {
    timer.current = setInterval(() => {
      loadThreads();
      if (openPhone) loadThread(openPhone);
    }, REFRESH_MS);
    return () => clearInterval(timer.current);
  }, [loadThreads, loadThread, openPhone]);

  const open = (phone) => {
    setOpenPhone(phone);
    setThread(null);
    setReply("");
    loadThread(phone);
    setThreads((list) => list.map((t) => (t.phone === phone ? { ...t, unread: 0 } : t)));
  };

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/whatsapp/inbox/${openPhone}/reply`, { text: reply });
      setReply("");
      loadThread(openPhone);
      loadThreads();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const unreadTotal = threads.reduce((sum, t) => sum + (t.unread || 0), 0);

  return (
    <div className="rounded-2xl border border-white/10 bg-[#12141A] p-5" data-testid="inbox-box">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-display text-lg font-bold">WhatsApp chats (+91 73008 77279)</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Customers who write to your business number appear here — reply within 24 hours
          </p>
        </div>
        <span className="font-mono text-[11px] text-cyan-300" data-testid="inbox-unread">
          {unreadTotal ? `${unreadTotal} new` : `${threads.length} chats`}
        </span>
      </div>

      {!connected && (
        <p className="mt-3 rounded-xl border border-yellow-300/25 bg-yellow-300/5 p-3 text-xs text-yellow-100" data-testid="inbox-not-connected">
          Connect your WhatsApp Business account to receive chats here.
        </p>
      )}

      {connected && (
        <div className="mt-4 grid gap-4 lg:grid-cols-[320px_1fr]">
          <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1" data-testid="inbox-threads">
            {threads.length === 0 && (
              <p className="text-xs text-slate-500" data-testid="inbox-empty">
                No chats yet. When a customer replies to your WhatsApp update, the message lands here.
              </p>
            )}
            {threads.map((t) => (
              <button
                key={t.phone}
                data-testid={`inbox-thread-${t.phone}`}
                onClick={() => open(t.phone)}
                className={`w-full rounded-xl border px-3.5 py-3 text-left transition-colors ${
                  openPhone === t.phone ? "border-cyan-400/50 bg-cyan-400/5" : "border-white/10 hover:border-white/25"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-bold text-slate-200">{t.name || `+${t.phone}`}</span>
                  {!!t.unread && (
                    <span data-testid={`inbox-unread-${t.phone}`} className="rounded-full bg-fuchsia-500 px-2 py-0.5 font-mono text-[10px] font-bold text-[#0A0B0E]">
                      {t.unread}
                    </span>
                  )}
                </div>
                <p className="mt-1 truncate text-xs text-slate-400">{t.last_text}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-600">
                  {sinceText(t.last_at)}{t.can_reply ? "" : " · reply window closed"}
                </p>
              </button>
            ))}
          </div>

          <div className="rounded-xl border border-white/10 bg-black/30 p-3" data-testid="inbox-thread-panel">
            {!openPhone && <p className="text-xs text-slate-500">Choose a chat on the left to read and reply.</p>}
            {openPhone && !thread && <p className="text-xs text-slate-500">Loading…</p>}
            {thread && (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 pb-2">
                  <span className="text-sm font-bold text-slate-200" data-testid="inbox-thread-name">
                    {thread.name || `+${thread.phone}`} <span className="font-mono text-[11px] text-slate-500">+{thread.phone}</span>
                  </span>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.15em] ${thread.can_reply ? "text-green-400" : "text-yellow-300"}`} data-testid="inbox-window-state">
                    {thread.can_reply ? `${hoursLeft(thread.last_in_at)} hr left to reply` : "Reply window closed"}
                  </span>
                </div>

                <div className="my-3 max-h-[300px] space-y-2 overflow-y-auto" data-testid="inbox-messages">
                  {thread.messages.map((m, i) => (
                    <div
                      key={`${m.created_at}-${i}`}
                      data-testid={`inbox-message-${i}`}
                      className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                        m.direction === "out"
                          ? "ml-auto bg-cyan-400/15 text-cyan-50"
                          : "bg-white/5 text-slate-200"
                      }`}
                    >
                      {m.media_url && m.kind === "image" && (
                        <a href={m.media_url} target="_blank" rel="noreferrer" data-testid={`inbox-photo-${i}`}>
                          <img src={m.media_url} alt="Customer photo" className="mb-2 max-h-64 w-full rounded-lg border border-white/10 object-contain" />
                        </a>
                      )}
                      {m.media_url && (m.kind === "video" || m.kind === "sticker") && (
                        m.kind === "video" ? (
                          <video src={m.media_url} controls data-testid={`inbox-video-${i}`} className="mb-2 max-h-64 w-full rounded-lg border border-white/10" />
                        ) : (
                          <img src={m.media_url} alt="Sticker" className="mb-2 h-24 w-24 object-contain" />
                        )
                      )}
                      {m.media_url && (m.kind === "audio" || m.kind === "voice") && (
                        <audio src={m.media_url} controls data-testid={`inbox-audio-${i}`} className="mb-2 w-full" />
                      )}
                      {m.media_url && m.kind === "document" && (
                        <a href={m.media_url} target="_blank" rel="noreferrer" data-testid={`inbox-file-${i}`} className="mb-2 flex items-center gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-xs text-cyan-300">
                          <FileText size={14} /> {m.media_name || "Open file"}
                        </a>
                      )}
                      {!m.media_url && m.kind !== "text" && (
                        <p className="mb-1 text-[11px] text-yellow-300" data-testid={`inbox-media-missing-${i}`}>
                          Ye {m.kind} WhatsApp se save nahi ho paya — customer se dobara bhejne ko kahiye
                        </p>
                      )}
                      <p className="whitespace-pre-wrap">{m.text}</p>
                      {m.media_url && (
                        <a href={m.media_url} download target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-cyan-300" data-testid={`inbox-download-${i}`}>
                          <Download size={11} /> Download
                        </a>
                      )}
                      <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.15em] text-slate-500">
                        {new Date(m.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>

                {thread.can_reply ? (
                  <div className="flex items-end gap-2">
                    <textarea
                      data-testid="inbox-reply-input"
                      value={reply}
                      rows={2}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Type your reply…"
                      className="flex-1 rounded-xl border border-white/10 bg-[#0A0B0E] px-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
                    />
                    <button
                      data-testid="inbox-reply-send"
                      onClick={send}
                      disabled={busy || !reply.trim()}
                      className="rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 px-5 py-2.5 text-sm font-bold text-[#0A0B0E] disabled:opacity-40"
                    >
                      {busy ? "Sending…" : "Send"}
                    </button>
                  </div>
                ) : (
                  <p className="rounded-xl border border-yellow-300/25 bg-yellow-300/5 p-3 text-xs text-yellow-100" data-testid="inbox-window-closed">
                    WhatsApp allows a free reply only for 24 hours after the customer writes. Send them an approved update from the box above, and reply here as soon as they answer.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
